import { useState, useEffect } from "react";
import {
  useQuery,
  hashQueryKey,
  QueryClient,
  QueryClientProvider as QueryClientProviderBase,
} from "react-query";
import {
  getFirestore,
  onSnapshot,
  doc,
  collection,
  query,
  where,
  orderBy,
  writeBatch,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
  getDocs,
  FieldValue,
  arrayUnion,
  arrayRemove,
  arrayContains,
  deleteField,
} from "firebase/firestore";
import { firebaseApp } from "./firebase";

// Initialize Firestore
const db = getFirestore(firebaseApp);

// React Query client
const client = new QueryClient(({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    }
  }
}));


// Day/Date Helpers
const pad2 = (n) => String(n).padStart(2, "0");
const toDayKey = (dateLike) => {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const dayKeyFromMs = (ms) => toDayKey(new Date(ms));


/**** USERS ****/

// Watch a user document for deletion or deactivation
// export const watchUserDoc = (uid, callback) => {
//   const userDocRef = doc(db, "users", uid);
//   const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
//     if (!docSnap.exists()) {
//       callback();
//     }
//   });
//   return unsubscribe;
// };

export const watchUserDoc = (uid, userCreationTime, onDeleted) => {
  const userDocRef = doc(db, "users", uid);
  const SAFE_WINDOW_MS = 3000; // 3 seconds

  const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
    if (!docSnap.exists()) {
      const creationTime = new Date(userCreationTime).getTime();
      const now = Date.now();
      if (now - creationTime < SAFE_WINDOW_MS) return; // skip false positive
      try {
        onDeleted(); // safe callback
      } catch (err) {
        console.error("Error in onDeleted callback:", err);
      }
    }
  });

  return unsubscribe;
};

// Subscribe to user data
// Note: This is called automatically in `auth.js` and data is merged into `auth.user`
export function useUser(uid) {
  // Manage data fetching with React Query: https://react-query.tanstack.com/overview
  return useQuery(
    // Unique query key: https://react-query.tanstack.com/guides/query-keys
    ["user", { uid }],
    // Query function that subscribes to data and auto-updates the query cache
    createQuery(() => doc(db, "users", uid)),
    // Only call query function if we have a `uid`
    { enabled: !!uid }
  );
}

// Fetch user data once (non-hook)
// Useful if you need to fetch data from outside of a component
export function getUser(uid) {
  return getDoc(doc(db, "users", uid)).then(format);
}

// Create a new user
export function createUser(uid, data) {
  return setDoc(doc(db, "users", uid), data, { merge: true });
}


export async function updateUser(uid, data) {
  if (!uid) return;
  try {
    return await updateDoc(doc(db, "users", uid), data);
  } catch (err) {
    if (err.code === "permission-denied") {
      return; // silent exit on signout
    }
    throw err;
  }
}


// Delete a user
// export function deleteUserDoc(uid) {
//   return deleteDoc(doc(db, "users", uid))
//     .then(() => {
//       console.log(`User with UID ${uid} has been deleted.`);
//     })
//     .catch(error => {
//       console.error("Error deleting user:", error);
//       throw error;
//     });
// }

/**** PRODUCTIONS ****/
/* Example query functions (modify to your needs) */

// Subscribe to production data
export function useProduction(id) {
  return useQuery(
    ["production", { id }],
    createQuery(() => doc(db, "productions", id)),
    { enabled: !!id }
  );
}

// Fetch production data once
export function useProductionOnce(id) {
  return useQuery(
    ["production", { id }],
    // When fetching once there is no need to use `createQuery` to setup a subscription
    // Just fetch normally using `getDoc` so that we return a promise
    () => getDoc(doc(db, "productions", id)).then(format),
    { enabled: !!id }
  );
}

// Subscribe to all productions by owner
export function useProductionsByOwner(uid) {
  return useQuery(
    ["productions", { uid }],
    createQuery(() =>
      query(
        collection(db, "productions"),
        where("uid", "==", uid),
        orderBy("createdAt", "desc")
      )
    ),
    { enabled: !!uid }
  );
}

// export async function createProduction(uid, data) {
//   console.log("Starting createProduction...");

//   try {
//     // ✅ Safe to create new production
//     const newProductionRef = await addDoc(collection(db, "productions"), {
//       uid,
//       ...data,
//       createdAt: serverTimestamp(),
//     });

//     console.log("Production created:", newProductionRef.id);

//     return {
//       success: true,
//       id: newProductionRef.id,
//       message: "Production created successfully.",
//     };
//   } catch (error) {
//     console.error("Error creating production:", error);
//     return {
//       success: false,
//       message: "Failed to create production. Please try again later.",
//       error: error.message,
//     };
//   }
// }


// Update a production
export function updateProduction(id, data) {
  return updateDoc(doc(db, "productions", id), data);
}

// Delete a production
// export function deleteProduction(id) {
//   return deleteDoc(doc(db, "productions", id))
//   .then(() => {
//     console.log(`Production with ID ${id} has been deleted.`);
//   })
//   .catch(error => {
//     console.error("Error deleting production:", error);
//     throw error;
//   });
// }

// Create a new user key
// export function createUserKeys(uid, data) {
//   return addDoc(collection(db, "userKeys"),
//   {
//     uid: uid,
//     ...data,
//     createdAt: serverTimestamp(),
//   });
// }

// Update a production key
// export function updateUserKeys(id, data) {
//   return updateDoc(doc(db, "userKeys", id), data);
// }


/**** LOCATION CATEGORIES ****/

// Subscribe to location data
export function useLocation(id) {
  return useQuery(
    ["location", { id }],
    createQuery(() => doc(db, "locations", id)),
    { enabled: !!id }
  );
}

// Fetch location data once
export function useLocationOnce(id) {
  return useQuery(
    ["location", { id }],
    // When fetching once there is no need to use `createQuery` to setup a subscription
    // Just fetch normally using `getDoc` so that we return a promise
    () => getDoc(doc(db, "locations", id)).then(format),
    { enabled: !!id }
  );
}

// Get all locations by production ID once
export async function getLocationsByProductionIdOnce(productionId) {
  const collectionRef = collection(db , "locations")
  const queryLocations = await getDocs(query(collectionRef, where("productionId", '==', productionId), orderBy("createdAt", "desc")));
  const fetchedLocations = queryLocations.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  return fetchedLocations
  } 

// Get all groups by production ID once
export async function getGroupsByProductionIdOnce(productionId) {
  const collectionRef = collection(db , "locationGroups")
  const queryGroups = await getDocs(query(collectionRef, where("productionId", '==', productionId), orderBy("createdAt", "desc")));
  const fetchedGroups = queryGroups.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  return fetchedGroups
  } 

export function useLocationsTypeProduction(productionId) {
  const [locations, setLocations] = useState([]);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!productionId) {
      setLocations([]);
      setStatus("idle");
      return;
    }

    setStatus("loading");

    // Subscribe to locations for the production
    const locQuery = query(
      collection(db, "locations"),
      where("productionId", "==", productionId),
      where("category", "==", "production"),
      orderBy("createdAt", "desc")
    );

    let groupListeners = new Map(); // key = groupId, value = unsubscribe

    const unsubscribeLocations = onSnapshot(
      locQuery,
      (locSnapshot) => {
        const locData = locSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
        setLocations((prev) => {
          // Keep old groupSortNumbers if possible
          return locData.map((loc) => {
            const existing = prev.find((p) => p.id === loc.id);
            return { ...loc, groupSortNumber: existing?.groupSortNumber ?? 0 };
          });
        });

        // Set up listeners for any new groups
        const uniqueGroupIds = [...new Set(locData.map((loc) => loc.groupId).filter(Boolean))];
        uniqueGroupIds.forEach((groupId) => {
          if (!groupListeners.has(groupId)) {
            const groupRef = doc(db, "locationGroups", groupId);
            const unsub = onSnapshot(groupRef, (groupSnap) => {
              if (groupSnap.exists()) {
                const sortOrder = groupSnap.data().sortOrder ?? 0;
                setLocations((prev) =>
                  prev.map((loc) =>
                    loc.groupId === groupId ? { ...loc, groupSortNumber: sortOrder } : loc
                  )
                );
              }
            });
            groupListeners.set(groupId, unsub);
          }
        });

        // Remove listeners for groups that no longer exist in locations
        Array.from(groupListeners.keys()).forEach((groupId) => {
          if (!uniqueGroupIds.includes(groupId)) {
            groupListeners.get(groupId)?.();
            groupListeners.delete(groupId);
          }
        });

        setStatus("success");
      },
      (err) => {
        console.error("Locations snapshot error:", err);
        setError(err);
        setStatus("error");
      }
    );

    // Cleanup all listeners on unmount
    return () => {
      unsubscribeLocations();
      groupListeners.forEach((unsub) => unsub());
      groupListeners.clear();
    };
  }, [productionId]);

  return { data: locations, status, error };
}

// Subscribe to all locations by owner
export function useLocationsByGroupId(groupId, productionId) {
  return useQuery(
    ["locations", { groupId, productionId }],
    createQuery(() =>
      query(
        collection(db, "locations"),
        where("groupId", "==", groupId),
        where("productionId", "==", productionId), // <-- added
        orderBy("createdAt", "desc")
      )
    ),
    { enabled: !!groupId && !!productionId }
  );
}

// Create a new location
export async function createLocation(data) {
  try {
    const dayKey =
      data.dayKey ??
      // temporary bridge during migration (ok to keep for now)
      (data.startDay ?? null) ??
      // legacy "day" string (ensure it parses as Date)
      (data.day ? toDayKey(new Date(data.day)) : null) ??
      // legacy ms fallback
      (data.startDate != null ? dayKeyFromMs(data.startDate) : null);

    await addDoc(collection(db, "locations"), {
      ...data,
      ...(dayKey ? { dayKey } : {}),
      createdAt: serverTimestamp(),
    });

    return { success: true, message: "Location created successfully" };
  } catch (error) {
    console.error("Error creating location: ", error);
    return { success: false, message: "Failed to create location. Please try again later." };
  }
}

// Create a new location from copy
export async function createLocationFromCopy(data, timeStamp) {
  const dayKey =
    data.dayKey ??
    // temporary bridge during migration:
    (data.startDay ?? null) ??
    (data.startDate != null ? dayKeyFromMs(data.startDate) : null);

  return addDoc(collection(db, "locations"), {
    ...data,
    ...(dayKey ? { dayKey } : {}),
    createdAt: timeStamp ?? serverTimestamp(),
  });
}

// Update a location
export async function updateLocation(id, data) {
  try {
    // Try updating the document
    await updateDoc(doc(db, "locations", id), data);
    console.log("Location updated successfully");
    return { success: true, message: "Location updated successfully" };
  } catch (error) {
    // Catch any errors and handle them
    console.error("Error updating location: ", error);
    return { success: false, message: "Failed to update location. Please try again later." };
  }
}

// Delete a location
export async function deleteLocation(id) {
  try {
    // Try updating the document
    await deleteDoc(doc(db, "locations", id));
    console.log("Location deleted successfully");
    return { success: true, message: "Location deleted successfully" };
  } catch (error) {
    // Catch any errors and handle them
    console.error("Error deleting location: ", error);
    return { success: false, message: "Failed to delete location. Please try again later." };
  }
}

export async function deleteLocationsByGroup(groupId, productionId) {
  if (!groupId || !productionId) throw new Error("Missing groupId or productionId");

  const locQuery = query(
    collection(db, "locations"),
    where("groupId", "==", groupId),
    where("productionId", "==", productionId)
  );

  const querySnapshot = await getDocs(locQuery);

  const deletedIds = [];

  for (const docSnap of querySnapshot.docs) {
    await deleteDoc(doc(db, "locations", docSnap.id));
    deletedIds.push(docSnap.id);
  }

  return deletedIds; // IDs that were actually deleted
}

export async function checkIfLocationIsUsingACustomType(locationGroupID, custType) {
  const collectionRef = collection(db, "locations");
  const querySnapshot = await getDocs(query(collectionRef, where("groupId", "==", locationGroupID)));

  for (const doc of querySnapshot.docs) {
    const data = doc.data();
    if (data.type === custType) {
      return true;
    }
  }  
  return false;
}

/**** LOCATION GROUPS ****/

// Subscribe to all location groups by production
export function useLocationGroupsByProductionID(productionId) {
  return useQuery(
    ["locationGroups", { productionId }],
    createQuery(() =>
      query(
        collection(db, "locationGroups"),
        where("productionId", "==", productionId),
        orderBy("createdAt", "asc")
      ),
      // Format function ensures new object references for each doc
      (querySnapshot) => {
        return querySnapshot.docs.map(docSnap => {
          const data = docSnap.data();
          return { id: docSnap.id, ...data }; // new object reference
        });
      }
    ),
    { enabled: !!productionId }
  );
}

// Create a new location group
export async function createLocationGroup(data) {
  try {
    // Ensure day keys exist (prefer explicit day keys)
    const startDay =
      data.startDay ?? (data.startDate != null ? dayKeyFromMs(data.startDate) : null);
    const endDay =
      data.endDay ?? (data.endDate != null ? dayKeyFromMs(data.endDate) : startDay);

    const docRef = await addDoc(collection(db, "locationGroups"), {
      ...data,
      ...(startDay ? { startDay } : {}),
      ...(endDay ? { endDay } : {}),
      createdAt: serverTimestamp(),
    });

    console.log("Location group created successfully!");
    return { success: true, id: docRef.id, message: "Location group created successfully" };
  } catch (error) {
    console.error("Error creating location group: ", error);
    return {
      success: false,
      message: "Failed to create location group. Please try again later.",
    };
  }
}

// Copy a location group
export async function copyLocationGroup(data, locs) {
  try {
    const startDay =
      data.startDay ?? (data.startDate != null ? dayKeyFromMs(data.startDate) : null);
    const endDay =
      data.endDay ?? (data.endDate != null ? dayKeyFromMs(data.endDate) : startDay);

    const res = await addDoc(collection(db, "locationGroups"), {
      ...data,
      ...(startDay ? { startDay } : {}),
      ...(endDay ? { endDay } : {}),
      createdAt: serverTimestamp(),
    });

    const newGroupId = res.id;

    for (const loc of locs) {
      const newLocationData = {
        productionId: loc.productionId,
        productionName: loc.productionName,
        category: loc.category,
        mapData: loc.mapData,
        name: loc.name,
        type: loc.type,
        typeOrder: loc.typeOrder,
        groupId: newGroupId,
        setName: loc.setName ?? null,

        // ✅ Location day identity (timezone-proof)
        ...(startDay ? { dayKey: startDay } : {}),

        // ⛑️ legacy compatibility during migration (optional)
        ...(data.startDate != null && { startDate: data.startDate }),
        ...(data.endDate != null && { endDate: data.endDate }),
      };

      await createLocationFromCopy(newLocationData, loc.createdAt);
    }

    return { success: true, message: "Location group copied successfully" };
  } catch (error) {
    console.error("Error copying location group: ", error);
    return {
      success: false,
      message: "Failed to copy location group. Please try again later.",
    };
  }
}

// Update a location group
export async function updateLocationGroup(id, data) {
  try {
    const patch = { ...data };

    // If timestamps are being updated but day keys aren't provided, derive them
    if (patch.startDay == null && patch.startDate != null) {
      patch.startDay = dayKeyFromMs(patch.startDate);
    }

    if (patch.endDay == null) {
      if (patch.endDate != null) {
        patch.endDay = dayKeyFromMs(patch.endDate);
      } else if (patch.startDay != null) {
        // Common: single-day group
        patch.endDay = patch.startDay;
      }
    }

    await updateDoc(doc(db, "locationGroups", id), patch);

    console.log("Location group updated successfully");
    return { success: true, message: "Location group updated successfully" };
  } catch (error) {
    console.error("Error updating location group: ", error);
    return {
      success: false,
      message: "Failed to update location group. Please try again later.",
    };
  }
}

// Add custom location type to a location group
export async function updateLocationGroupCustomTypes(id, data) {
  return updateDoc(doc(db, "locationGroups", id), {'customTypes': arrayUnion(data)});
// let docRef = await db.collection('locationGroups').doc(id);
// return await docRef.update({customTypes: firebase.firestore.FieldValue.arrayUnion(data)});
}

// Add custom location type to a location group
export async function deleteLocationGroupCustomType(id, data) {
  // console.log('here in the db! try to delete ' + JSON.stringify(data))
  return updateDoc(doc(db, "locationGroups", id), {'customTypes': arrayRemove(data)});
}

// Delete a location group
export async function deleteLocationGroup(id) {
  try {
    // Try updating the document
    await deleteDoc(doc(db, "locationGroups", id));
    console.log("Location group deleted successfully");
    return { success: true, message: "Location group deleted successfully" };
  } catch (error) {
    // Catch any errors and handle them
    console.error("Error deleting location group: ", error);
    return { success: false, message: "Failed to delete location group. Please try again later." };
  }
}

// Delete Phone Number From Location Group
export async function deletePhoneFromLocationGroup(id) {
  try {
    // Try updating the document
    await updateDoc(doc(db, "locationGroups", id), {'lostCallNumber': deleteField(), 'lostCallName': deleteField()});
    console.log("Lost contact info deleted successfully");
    return { success: true, message: "Phone contact deleted successfully" };
  } catch (error) {
    // Catch any errors and handle them
    console.error("Error deleting lost contact info: ", error);
    return { success: false, message: "Error deleting phone contact. Please try again later." };
  }
}

// Delete Special Instructions From Location Group
export async function deleteSpecialInstructionsFromLocationGroup(id) {
  try {
    // Try updating the document
    await updateDoc(doc(db, "locationGroups", id), {'specialInstructions': deleteField()});
    console.log("Special instructions info deleted successfully");
    return { success: true, message: "Special instructions deleted successfully" };
  } catch (error) {
    // Catch any errors and handle them
    console.error("Error deleting special instructions: ", error);
    return { success: false, message: "Error deleting special instructions. Please try again later." };
  }
}

/***** SIGN UP HELPERS *****/
// MOVED TO SERVER

// Store Firestore unsubscribe functions
const unsubs = {};

function createQuery(getRef) {
  // Create a query function to pass to `useQuery`
  return async ({ queryKey }) => {
    let unsubscribe;
    let firstRun = true;
    // Wrap `onSnapshot` with a promise so that we can return initial data
    const data = await new Promise((resolve, reject) => {
      unsubscribe = onSnapshot(
        getRef(),
        // Success handler resolves the promise on the first run.
        // For subsequent runs we manually update the React Query cache.
        (response) => {
          const data = format(response);
          if (firstRun) {
            firstRun = false;
            resolve(data);
          } else {
            client.setQueryData(queryKey, data);
          }
        },
        // Error handler rejects the promise on the first run.
        // We can't manually trigger an error in React Query, so on a subsequent runs we
        // invalidate the query so that it re-fetches and rejects if error persists.
        (error) => {
          if (firstRun) {
            firstRun = false;
            reject(error);
          } else {
            client.invalidateQueries(queryKey);
          }
        }
      );
    });

    // Unsubscribe from an existing subscription for this `queryKey` if one exists
    // Then store `unsubscribe` function so it can be called later
    const queryHash = hashQueryKey(queryKey);
    unsubs[queryHash] && unsubs[queryHash]();
    unsubs[queryHash] = unsubscribe;

    return data;
  };
}

// Automatically remove Firestore subscriptions when all observing components have unmounted
client.queryCache.subscribe(({ type, query }) => {
  if (
    type === "observerRemoved" &&
    query.getObserversCount() === 0 &&
    unsubs[query.queryHash]
  ) {
    // Call stored Firestore unsubscribe function
    unsubs[query.queryHash]();
    delete unsubs[query.queryHash];
  }
});

// Format Firestore response
function format(response) {
  // Converts doc into object that contains data and `doc.id`
  const formatDoc = (doc) => ({ id: doc.id, ...doc.data() });
  if (response.docs) {
    // Handle a collection of docs
    return response.docs.map(formatDoc);
  } else {
    // Handle a single doc
    return response.exists() ? formatDoc(response) : null;
  }
}

// React Query context provider that wraps our app
export function QueryClientProvider(props) {
  return (
    <QueryClientProviderBase client={client}>
      {props.children}
    </QueryClientProviderBase>
  );
}
